import { useState, useEffect } from "react";
import API from "../../services/api";

interface AppSetting {
  _id: string;
  key: string;
  value: string;
  description: string;
  status: 'active' | 'inactive';
}

interface EditSettingModalProps {
  setting: AppSetting;
  onClose: () => void;
  onSuccess: () => void;
}

interface ValidationError {
  type: string;
  msg: string;
  path: string;
  location: string;
}

export default function EditSettingModal({ setting, onClose, onSuccess }: EditSettingModalProps) {
  const [formData, setFormData] = useState({
    key: "",
    value: "",
    description: "",
    status: "active"
  });
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  useEffect(() => {
    setFormData({
      key: setting.key,
      value: setting.value,
      description: setting.description || "",
      status: setting.status
    });
  }, [setting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setFieldErrors({});
    setGeneralError(null);

    try {
      // Remove key from formData when submitting since it's read-only
      const { key, ...submitData } = formData;
      const res = await API.put(`/setting/${setting._id}`, submitData);
      if (res.data && res.data.success) {
        onSuccess();
      }
    } catch (error: any) {
      if (error?.response?.data?.data && Array.isArray(error.response.data.data)) {
        const errors: { [key: string]: string } = {};
        error.response.data.data.forEach((err: ValidationError) => {
          errors[err.path] = err.msg;
        });
        setFieldErrors(errors);
      } else {
        setGeneralError(error?.response?.data?.message || "Failed to update setting");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (fieldErrors[e.target.name]) {
      setFieldErrors({
        ...fieldErrors,
        [e.target.name]: ""
      });
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Edit Setting</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Key *</label>
            <input
              type="text"
              name="key"
              value={formData.key}
              disabled={true}  // Disable the key field
              style={{
                ...styles.input,
                ...styles.inputDisabled,  // Add disabled styles
                ...(fieldErrors.key ? styles.inputError : {})
              }}
            />
            <small style={styles.helperText}>Key cannot be changed</small>
            {fieldErrors.key && (
              <span style={styles.fieldError}>{fieldErrors.key}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Value *</label>
            <textarea
              name="value"
              value={formData.value}
              onChange={handleChange}
              placeholder="Enter setting value"
              rows={4}
              style={{
                ...styles.textarea,
                ...(fieldErrors.value ? styles.inputError : {})
              }}
            />
            {fieldErrors.value && (
              <span style={styles.fieldError}>{fieldErrors.value}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Description <span style={styles.optional}>(optional)</span></label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Brief description of this setting"
              style={{
                ...styles.input,
                ...(fieldErrors.description ? styles.inputError : {})
              }}
            />
            {fieldErrors.description && (
              <span style={styles.fieldError}>{fieldErrors.description}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              style={styles.select}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {generalError && (
            <div style={styles.errorMessage}>
              {generalError}
            </div>
          )}

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button type="submit" disabled={loading} style={styles.submitButton}>
              {loading ? "Updating..." : "Update Setting"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    maxWidth: "600px",
    width: "90%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  modalTitle: {
    fontSize: "22px",
    fontWeight: "600",
    color: "#1a2332",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#666",
    padding: "0 4px",
  },
  formGroup: {
    marginBottom: "16px",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#555",
    marginBottom: "6px",
  },
  optional: {
    fontSize: "12px",
    fontWeight: "400",
    color: "#999",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  },
  inputDisabled: {
    backgroundColor: "#f5f5f5",
    color: "#999",
    cursor: "not-allowed",
    borderColor: "#e0e0e0",
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
    resize: "vertical" as const,
    fontFamily: "inherit",
  },
  inputError: {
    borderColor: "#f44336",
    backgroundColor: "#fff3f3",
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "#fff",
  },
  helperText: {
    display: "block",
    marginTop: "4px",
    fontSize: "12px",
    color: "#888",
  },
  fieldError: {
    display: "block",
    marginTop: "4px",
    fontSize: "12px",
    color: "#f44336",
  },
  errorMessage: {
    padding: "10px 14px",
    backgroundColor: "#fff3f3",
    border: "1px solid #f44336",
    borderRadius: "6px",
    color: "#d32f2f",
    fontSize: "14px",
    marginBottom: "16px",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "24px",
    paddingTop: "16px",
    borderTop: "1px solid #e8ecf1",
  },
  cancelButton: {
    padding: "10px 20px",
    backgroundColor: "#f5f5f5",
    color: "#666",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  submitButton: {
    padding: "10px 24px",
    backgroundColor: "#6c5ce7",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.2s",
  },
};